import { useEffect } from 'react';

import { AxiosInstance, AxiosRequestConfig, isAxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import jwtDecode from 'jwt-decode';

import { RPC_ERROR_CODES } from 'src/apis/exception';
import { RPC_BAD_CREDENTIALS_EXCEPTION_REASON_TYPES } from 'src/apis/exception/RPCBadCredentialsException';
import { isDefinedRPCException, isRPCErrorResponse } from 'src/apis/utils';
import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';
import {
  addRetryPromiseAtom,
  azarTokenAtom,
  renewAccessTokenAtom,
  loginWithTokenAtom,
  removeTokenAtom,
  userDataAtom,
} from 'src/stores/auth/atoms';
import { client, rpcAxios } from 'src/utils/api';
import { pushAppsflyerEvent, setAppsflyerCustomerUserId } from 'src/utils/appsflyer/pba';
import { ControlledPromise } from 'src/utils/controlled-promise';

interface Token {
  exp: number;
}

const checkExpire = (exp: number) => {
  const now = new Date().getTime();
  return now > exp * 1000;
};

const retry = async (
  promise: ControlledPromise<void>,
  axiosInstance: AxiosInstance,
  config?: AxiosRequestConfig
) => {
  await promise.promise;
  return axiosInstance({ ...config, isRetryRequest: true });
};

const useSession = () => {
  const azarToken = useAtomValue(azarTokenAtom);
  const renewAccessToken = useSetAtom(renewAccessTokenAtom);
  const loginWithToken = useSetAtom(loginWithTokenAtom);
  const addRetryPromise = useSetAtom(addRetryPromiseAtom);
  const removeToken = useSetAtom(removeTokenAtom);
  const userData = useAtomValue(userDataAtom);

  useEffect(() => {
    if (azarToken?.refreshToken) {
      const isRefreshTokenExpired = checkExpire(jwtDecode<Token>(azarToken.refreshToken).exp);
      if (isRefreshTokenExpired) {
        removeToken();
      }
    }
  }, [azarToken, removeToken]);

  useIsomorphicLayoutEffect(() => {
    const rpcInterceptor = rpcAxios.interceptors.response.use(
      async (response) => {
        if (!isRPCErrorResponse(response.data) || response.config.isRetryRequest) {
          return response;
        }

        const { error } = response.data;

        if (
          isDefinedRPCException(error, RPC_ERROR_CODES.AUTHENTICATION_ERROR) &&
          error.data?.throwable.reason === 'default_reason'
        ) {
          const promise = new ControlledPromise<void>();
          addRetryPromise({ promise });
          loginWithToken();
          return retry(promise, rpcAxios, response.config);
        }

        if (
          isDefinedRPCException(error, RPC_ERROR_CODES.BAD_CREDENTIALS) &&
          error.data?.throwable.reason === RPC_BAD_CREDENTIALS_EXCEPTION_REASON_TYPES.EXPIRED_TOKEN
        ) {
          const promise = new ControlledPromise<void>();
          addRetryPromise({ promise });
          renewAccessToken();
          return retry(promise, rpcAxios, response.config);
        }

        return response;
      },
      async (error) => {
        return Promise.reject(error);
      }
    );
    return () => {
      rpcAxios.interceptors.response.eject(rpcInterceptor);
    };
  }, [addRetryPromise, loginWithToken, renewAccessToken]);

  useIsomorphicLayoutEffect(() => {
    const clientInterceptor = client.interceptors.response.use(
      async (response) => response,
      async (error) => {
        if (
          isAxiosError(error) &&
          error.response?.status === 401 &&
          !error.config?.isRetryRequest
        ) {
          const promise = new ControlledPromise<void>();
          addRetryPromise({ promise });
          renewAccessToken();
          return retry(promise, client, error.config);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      client.interceptors.response.eject(clientInterceptor);
    };
  }, [addRetryPromise, renewAccessToken]);

  useEffect(() => {
    if (userData?.userId) {
      setAppsflyerCustomerUserId(userData.userId);

      /*
       * Appsflyer login 이벤트는 로그인 모달을 통한 로그인 뿐만 아니라, 세션 유지로인한 자동 로그인도 포함합니다.
       * @see https://www.notion.so/hpcnt/Appsflyer-migration-1f9ce00cd35480d3ad4ec8bbd0f1fbcc?v=175ce00cd35481b6afe8000cee895597&source=copy_link#24dce00cd35480b1a0c3c32c295c82ad
       */
      pushAppsflyerEvent('login');
    }
  }, [userData?.userId]);

  useEffect(() => {
    // user-info api 필드 커뮤니케이션 오류로 userProfile이 없는 유저가 있고, userProfile.xx로 접근하면 크래시 나서 로그아웃 처리
    if (userData?.userId && !userData?.userProfile) {
      removeToken();
    }
  }, [removeToken, userData?.userId, userData?.userProfile]);
};

export default useSession;
